import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import DashboardService from '../services/DashboardService';

/**
 * DashboardPopupContext
 * 대시보드 팝업 관리를 위한 중앙 컨텍스트
 * 
 * 주요 기능:
 * 1. 팝업 스택 관리 (다단계 팝업 지원)
 * 2. 팝업 상태 추적 (위치, 크기, z-index)
 * 3. 팝업 간 통신 및 이벤트 관리
 * 4. 팝업 생명주기 관리
 */

const DashboardPopupContext = createContext();

export const useDashboardPopup = () => {
  const context = useContext(DashboardPopupContext);
  if (!context) {
    throw new Error('useDashboardPopup must be used within DashboardPopupProvider');
  }
  return context;
};

/**
 * 대시보드 팝업 자동 크기 계산 함수
 * 
 * ============================================================================
 * 핵심 아이디어: "최소 바운딩 박스(Minimum Bounding Box) + 종횡비 유지"
 * ============================================================================
 * 
 * 목표:
 *   - 차트들만 딱 맞게 보이는 최소 크기의 팝업 창을 만듭니다.
 *   - 차트들이 팝업 내에서 비율을 유지하며 꽉 차게 렌더링됩니다.
 *   - 불필요한 빈 공간을 최소화하고 컨텐츠 중심의 레이아웃을 구현합니다.
 * 
 * ============================================================================
 * 전체 알고리즘 흐름:
 * ============================================================================
 * 
 * [1단계] 바운딩 박스 계산
 *   - 모든 차트의 좌표(x, y, w, h)를 분석하여 최소/최대값 찾기
 *   - minX, minY: 차트들이 시작하는 최소 지점
 *   - maxX, maxY: 차트들이 끝나는 최대 지점
 *   - boxWidth = maxX - minX (차트들을 감싸는 너비)
 *   - boxHeight = maxY - minY (차트들을 감싸는 높이)
 * 
 * [2단계] 레이아웃 정규화 (0,0 기준 이동)
 *   - 모든 차트의 좌표를 (minX, minY)만큼 빼서 원점으로 이동
 *   - 예: 차트 A가 (3, 2, 5, 7) → (0, 0, 5, 7)
 *   - 예: 차트 B가 (5, 4, 4, 6) → (2, 2, 4, 6)
 *   - 결과: 빈 공간 없이 차트들이 좌측 상단부터 시작
 * 
 * [3단계] 팝업 크기 계산 (마진 고려한 정밀 계산)
 *   A. 팝업 너비 결정
 *      - targetWidth = 화면 너비의 50% (최대 1000px)
 *   
 *   B. 그리드 컬럼 너비 계산 (정사각형 그리드 구현)
 *      - containerWidth = targetWidth - 48px (패딩)
 *      - colWidth = (containerWidth - (cols-1) * margin) / cols
 *      - 이 공식은 react-grid-layout의 계산 방식과 동일
 *   
 *   C. 행(Row) 높이 설정 (정사각형 비율 유지)
 *      - rowHeight = colWidth
 *      - 이렇게 하면 1칸의 가로 길이 = 1칸의 세로 길이 (정사각형)
 *   
 *   D. 팝업 전체 높이 계산
 *      - gridHeight = (rowHeight + margin) * rows - margin
 *      - popupHeight = gridHeight + 80px (헤더 및 여백)
 * 
 * [4단계] 반환값
 *   - width, height: 팝업 창 크기 (px)
 *   - normalizedLayout: (0,0)으로 정규화된 차트 배치 정보
 *   - gridColumns: 그리드 컬럼 수 = boxWidth
 *   - overrideRowHeight: 계산된 행 높이 (정사각형 그리드용)
 * 
 * ============================================================================
 * 실제 렌더링 과정:
 * ============================================================================
 * 
 * 1. DashboardPopup 컴포넌트가 계산된 width/height로 팝업 창을 생성
 * 2. Dashboard 컴포넌트가 normalizedLayout과 overrideRowHeight를 받음
 * 3. DashboardGrid가 정규화된 좌표로 차트들을 배치
 * 4. 각 차트가 정사각형 그리드에 맞춰 비율을 유지하며 렌더링
 * 
 * 결과: 차트들이 팝업 창을 꽉 채우면서도 원래 비율을 유지하는 최적화된 레이아웃
 * 
 * ============================================================================
 * 
 * @param {Object} dashboardTemplate - 대시보드 템플릿 (components 포함)
 * @returns {Object} { width, height, normalizedLayout, gridColumns, overrideRowHeight }
 */
const calculateAutoSizeFromTemplate = (dashboardTemplate) => {
  const template = dashboardTemplate?.template || dashboardTemplate;
  const components = template?.components || [];
  
  // ===== [예외 처리] 컴포넌트가 없으면 기본 크기 반환 =====
  if (!components || components.length === 0) {
    return { width: '50vw', height: '40vh', normalizedLayout: null };
  }

  // ============================================================================
  // [1단계] 최소 바운딩 박스(Minimum Bounding Box) 계산
  // ============================================================================
  // 목적: 
  //   모든 차트를 딱 감싸는 최소 사각형을 찾습니다.
  //   빈 공간(차트가 없는 영역)을 제외하기 위함입니다.
  // 
  // 동작 원리:
  //   - 각 차트의 시작 지점(x, y)과 끝 지점(x+w, y+h)을 확인
  //   - 모든 차트 중 가장 왼쪽(minX), 위쪽(minY), 오른쪽(maxX), 아래쪽(maxY) 찾기
  // 
  // 예시:
  //   차트 A: (x=3, y=2, w=5, h=7) → 시작(3,2), 끝(8,9)
  //   차트 B: (x=5, y=4, w=4, h=6) → 시작(5,4), 끝(9,10)
  //   
  //   계산 결과:
  //   - minX = 3 (가장 왼쪽 시작점)
  //   - minY = 2 (가장 위쪽 시작점)
  //   - maxX = 9 (가장 오른쪽 끝점)
  //   - maxY = 10 (가장 아래쪽 끝점)
  //   - boxWidth = 9 - 3 = 6 (최소 사각형의 너비, 그리드 단위)
  //   - boxHeight = 10 - 2 = 8 (최소 사각형의 높이, 그리드 단위)
  // ============================================================================
  
  let minX = Infinity, minY = Infinity, maxX = 0, maxY = 0;
  
  components.forEach(comp => {
    const pos = comp.position || comp.layout || {};
    const x = typeof pos.x === 'number' ? pos.x : 0;
    const y = typeof pos.y === 'number' ? pos.y : 0;
    const w = typeof pos.w === 'number' ? pos.w : 6;
    const h = typeof pos.h === 'number' ? pos.h : 4;
    
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + h);
  });

  const boxWidth = Math.max(1, maxX - minX);   // 최소 바운딩 박스 너비 (그리드 단위)
  const boxHeight = Math.max(1, maxY - minY);  // 최소 바운딩 박스 높이 (그리드 단위)

  // ============================================================================
  // [2단계] 레이아웃 정규화 (좌표 0점화)
  // ============================================================================
  // 목적:
  //   모든 차트를 (0,0)을 기준으로 이동시켜 빈 공간을 제거합니다.
  //   이렇게 하면 팝업 내에서 차트들이 좌측 상단부터 시작하게 됩니다.
  // 
  // 동작 원리:
  //   각 차트의 x, y 좌표에서 minX, minY를 빼서 원점 이동
  // 
  // 예시 (위 예시 계속):
  //   차트 A: (3, 2, 5, 7) → (3-3, 2-2, 5, 7) = (0, 0, 5, 7)
  //   차트 B: (5, 4, 4, 6) → (5-3, 4-2, 4, 6) = (2, 2, 4, 6)
  //   
  //   시각화:
  //   [기존]                    [정규화 후]
  //   0 1 2 3 4 5 6 7 8 9      0 1 2 3 4 5 6 7 8 9
  //   ┌─────────────────       ┌─────────────────
  // 0 │                       0 │
  // 1 │                       1 │
  // 2 │      ┌─────┐          2 │ ┌─────┐
  // 3 │      │  A  │          3 │ │  A  │
  // 4 │      │     │  ┌───    4 │ │     │  ┌───
  // 5 │      │     │  │ B     5 │ │     │  │ B
  // 6 │      │     │  │       6 │ │     │  │
  // 7 │      │     │  │       7 │ │     │  │
  // 8 │      └─────┘  │       8 │ └─────┘  │
  // 9 │               └───    9 │          └───
  //   
  //   결과: 좌측 3칸, 상단 2줄의 빈 공간이 제거됨
  // ============================================================================
  
  const normalizedLayout = components.map(comp => {
    const pos = comp.position || comp.layout || {};
    return {
      i: comp.id,
      x: (typeof pos.x === 'number' ? pos.x : 0) - minX,  // 원점으로 이동
      y: (typeof pos.y === 'number' ? pos.y : 0) - minY,  // 원점으로 이동
      w: typeof pos.w === 'number' ? pos.w : 6,
      h: typeof pos.h === 'number' ? pos.h : 4
    };
  });

  // ============================================================================
  // [3단계] 팝업 크기 계산 (종횡비 유지 + 화면 크기 제한 + 정사각형 그리드)
  // ============================================================================
  // 목적: 
  //   차트 비율을 유지하면서 화면에 맞는 크기로 팝업을 띄웁니다.
  // 
  // 핵심 원칙:
  //   ① 정사각형 그리드: 1칸의 가로 길이 = 1칸의 세로 길이 (unitSize)
  //   ② 종횡비 유지: boxWidth : boxHeight = 팝업 너비 : 팝업 높이
  //   ③ 화면 제한: 팝업이 화면을 벗어나지 않도록 85% 제한
  // 
  // 계산 흐름:
  //   [A] 너비 우선 접근: 화면의 50% → 높이 계산
  //   [B] 높이 초과 시: 높이를 85%로 제한 → 너비 역산
  // ============================================================================
  
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;
  
  // 최대 허용 크기 (화면의 85%)
  const maxWidth = windowWidth * 0.85;
  const maxHeight = windowHeight * 0.85;
  
  const gridSpacing = 10;  // react-grid-layout의 기본 마진 (x, y 동일)
  const paddingX = 48;     // 팝업 좌우 패딩 합계 (px)
  const paddingY = 60;     // 팝업 상하 패딩 + 헤더 높이 (px)
  
  // --- [3-A] 1차 시도: 너비 기준 계산 ---
  // 팝업 너비를 화면의 50%로 설정 (최대 1000px)
  let targetWidth = Math.min(1000, maxWidth);
  
  // 실제 그리드 컨테이너 너비 (패딩 제외)
  let contentWidth = targetWidth - paddingX;
  
  // 그리드 1칸의 크기 계산 (정사각형 단위)
  // react-grid-layout 공식: colWidth = (totalWidth - margin * (cols-1)) / cols
  // 
  // 예: contentWidth=952px, boxWidth=6, margin=10
  //   => unitSize = (952 - 10*5) / 6 = 902/6 = 150.33px
  //   => 각 그리드 칸이 150.33 x 150.33 정사각형
  let unitSize = (contentWidth - (boxWidth - 1) * gridSpacing) / boxWidth;
  
  // 전체 그리드 높이 계산
  // react-grid-layout 공식: totalHeight = (rowHeight + margin) * rows - margin
  // (마지막 행 아래의 마진은 제외)
  // 
  // 예: unitSize=150, boxHeight=8, margin=10
  //   => gridHeight = (150 + 10) * 8 - 10 = 1280 - 10 = 1270px
  const gridHeight = (unitSize + gridSpacing) * boxHeight - gridSpacing;
  
  // 팝업 전체 높이 = 그리드 높이 + 헤더/패딩
  let targetHeight = gridHeight + paddingY;
  
  // --- [3-B] 2차 조정: 높이가 화면을 넘어가면 높이 기준으로 역산 ---
  // 만약 계산된 높이가 화면 높이의 85%를 넘으면,
  // 높이를 maxHeight로 제한하고 이에 맞춰 너비를 축소합니다.
  if (targetHeight > maxHeight) {
    targetHeight = maxHeight;
    
    // 역산: 그리드 높이
    const contentHeight = targetHeight - paddingY;
    
    // unitSize 재계산 (높이 기준)
    // contentHeight = (unitSize + gridSpacing) * boxHeight - gridSpacing
    // => contentHeight + gridSpacing = (unitSize + gridSpacing) * boxHeight
    // => unitSize + gridSpacing = (contentHeight + gridSpacing) / boxHeight
    // => unitSize = ((contentHeight + gridSpacing) / boxHeight) - gridSpacing
    unitSize = ((contentHeight + gridSpacing) / boxHeight) - gridSpacing;
    
    // 너비 재계산 (종횡비 유지)
    contentWidth = (unitSize * boxWidth) + ((boxWidth - 1) * gridSpacing);
    targetWidth = contentWidth + paddingX;
  }
  
  // --- [3-C] 최종 rowHeight 설정 (정사각형 그리드 보장) ---
  // rowHeight = unitSize로 설정하면 1칸이 정사각형이 됩니다.
  // 이를 통해 대시보드의 원래 비율(boxWidth:boxHeight)이 그대로 유지됩니다.
  const overrideRowHeight = unitSize;

  // ============================================================================
  // [4단계] 결과 반환
  // ============================================================================
  // 
  // 반환값 설명:
  //   - width, height: 팝업 창의 픽셀 크기
  //     → DashboardPopup 컴포넌트가 이 크기로 팝업 창을 생성합니다.
  //   
  //   - normalizedLayout: (0,0) 기준으로 정규화된 차트 배치 정보
  //     → Dashboard 컴포넌트의 overrideLayout prop으로 전달됩니다.
  //     → 차트들이 좌측 상단부터 시작하여 빈 공간 없이 배치됩니다.
  //   
  //   - gridColumns: 그리드 컬럼 수 = boxWidth
  //     → Dashboard 컴포넌트의 overrideGridColumns prop으로 전달됩니다.
  //     → 예: 바운딩 박스가 6칸이면 그리드를 12칸이 아닌 6칸으로 설정
  //     → 결과: 차트들이 가로로 꽉 차게 됩니다.
  //   
  //   - overrideRowHeight: 계산된 그리드 행 높이 (px)
  //     → Dashboard → DashboardGrid로 전달되어 정사각형 그리드를 구현합니다.
  //     → 예: 1칸 너비가 150px이면 1칸 높이도 150px
  //     → 결과: 차트 비율이 왜곡되지 않고 원래 모양 그대로 유지됩니다.
  // 
  // 최종 효과:
  //   팝업 내부 차트들이 (0,0)부터 시작하여, 정사각형 그리드에 맞춰,
  //   팝업 창을 꽉 채우면서도 원래 비율을 유지하며 렌더링됩니다.
  // ============================================================================
  
  return {
    width: Math.round(targetWidth),              // 팝업 너비 (px)
    height: Math.round(targetHeight),            // 팝업 높이 (px)
    normalizedLayout,                            // 정규화된 레이아웃 (0,0 기준)
    gridColumns: boxWidth,                       // 오버라이드 컬럼 수
    overrideRowHeight: Math.round(overrideRowHeight) // 오버라이드 행 높이 (정사각형)
  };
};

// 기본 팝업 설정
const DEFAULT_POPUP_CONFIG = {
  size: {
    width: '80%',
    height: '70%',
    minWidth: 600,
    minHeight: 400,
    maxWidth: '95%',
    maxHeight: '95%'
  },
  position: 'center', // 'center', 'top', 'bottom', 'left', 'right', {x, y}
  animation: {
    type: 'fade', // 'fade', 'slide', 'zoom', 'none'
    duration: 300
  },
  backdrop: {
    blur: true,
    closeOnClick: true,
    opacity: 0.7
  },
  resizable: true,
  draggable: true,
  closeOnEscape: true,
  transparentMode: false // 투명 대시보드 모드: 팝업 프레임을 숨기고 차트만 표시
};

export const DashboardPopupProvider = ({ children }) => {
  // 팝업 스택 (배열로 관리하여 순서 보장)
  const [popups, setPopups] = useState([]);
  
  // 팝업 레퍼런스 저장 (DOM 조작용)
  const popupRefs = useRef({});
  
  // 활성 팝업 ID (포커스 관리)
  const [activePopupId, setActivePopupId] = useState(null);
  
  // 팝업 간 부모-자식 관계 추적
  const parentChildMap = useRef({});
  
  // 최대 팝업 개수 제한
  const MAX_POPUPS = 5;
  
  /**
   * config 또는 popup 객체에서 effectiveDashboardId를 추출하는 헬퍼 함수
   * dashboards 배열과 dashboardId 필드 모두 지원 (하위 호환성)
   * @param {Object} obj - config 또는 popup 객체
   * @returns {string|null} 유효한 대시보드 ID
   */
  const getEffectiveDashboardId = (obj) => {
    if (!obj) return null;
    // dashboardId가 있으면 우선 사용 (하위 호환)
    if (obj.dashboardId) return obj.dashboardId;
    // dashboards 배열이 있으면 첫 번째 항목의 id 사용
    if (obj.dashboards && Array.isArray(obj.dashboards) && obj.dashboards.length > 0) {
      return obj.dashboards[0].id || null;
    }
    return null;
  };
  
  /**
   * 팝업 열기 (내부 동기 함수)
   * @param {Object} config - 팝업 설정
   * @param {Object} resolvedSize - 미리 계산된 크기 (sizeMode: 'auto'일 때 사용)
   * @returns {string} 생성된 팝업 ID
   */
  const openPopupInternal = useCallback((config, resolvedSize = null) => {
    // 최대 팝업 개수 체크
    if (popups.length >= MAX_POPUPS) {
      console.warn('[DashboardPopup] Maximum popup limit reached:', MAX_POPUPS);
      alert(`최대 ${MAX_POPUPS}개의 팝업만 열 수 있습니다.`);
      return null;
    }
    
    // config에서 유효한 dashboardId 추출 (dashboards 배열 또는 dashboardId 필드)
    const effectiveDashboardId = getEffectiveDashboardId(config);
    
    // 순환 참조 체크 (A -> B -> A 방지)
    const checkCircularReference = (targetDashboardId, parentId) => {
      // dashboardId가 없으면 순환 참조 체크 불가 (스킵)
      if (!targetDashboardId || !parentId) return false;
      
      let currentParent = parentId;
      const visited = new Set();
      
      while (currentParent) {
        if (visited.has(currentParent)) return true;
        visited.add(currentParent);
        
        const parentPopup = popups.find(p => p.id === currentParent);
        if (parentPopup) {
          // 부모 팝업의 effectiveDashboardId도 동일하게 추출
          const parentDashboardId = getEffectiveDashboardId(parentPopup);
          if (parentDashboardId && parentDashboardId === targetDashboardId) {
            return true;
          }
        }
        currentParent = parentChildMap.current[currentParent];
      }
      
      return false;
    };
    
    if (checkCircularReference(effectiveDashboardId, config.parentId)) {
      console.warn('[DashboardPopup] Circular reference detected:', effectiveDashboardId);
      alert('순환 참조가 감지되었습니다. 이 대시보드는 이미 상위 팝업에서 열려 있습니다.');
      return null;
    }
    
    // 팝업 ID 생성
    const popupId = uuidv4();
    
    // 크기 결정: resolvedSize가 있으면 사용, 없으면 config.size 사용
    const finalSize = resolvedSize || config.size || {};
    
    // 팝업 설정 병합
    const mergedConfig = {
      ...DEFAULT_POPUP_CONFIG,
      ...config,
      size: {
        ...DEFAULT_POPUP_CONFIG.size,
        ...finalSize
      },
      animation: {
        ...DEFAULT_POPUP_CONFIG.animation,
        ...(config.animation || {})
      },
      backdrop: {
        ...DEFAULT_POPUP_CONFIG.backdrop,
        ...(config.backdrop || {})
      }
    };
    
    // 위치 계산
    const calculatePosition = (position, parentId) => {
      // 명시적 좌표가 설정된 경우 그대로 사용
      if (typeof position === 'object' && position.x !== undefined && position.y !== undefined) {
        return position;
      }
      
      // 기본 위치 계산 (중첩 팝업도 동일하게 중앙 배치)
      switch (position) {
        case 'center':
          return { x: 'center', y: 'center' };
        case 'top':
          return { x: 'center', y: 50 };
        case 'bottom':
          return { x: 'center', y: 'auto' };
        case 'left':
          return { x: 50, y: 'center' };
        case 'right':
          return { x: 'auto', y: 'center' };
        default:
          return { x: 'center', y: 'center' };
      }
    };
    
    // 새 팝업 객체 생성 (멀티 대시보드 지원)
    const newPopup = {
      id: popupId,
      // 단일 대시보드 모드와 멀티 대시보드 모드 모두 지원
      // effectiveDashboardId를 사용하여 dashboards 배열에서도 dashboardId를 설정
      dashboardId: effectiveDashboardId,
      dashboards: config.dashboards || null, // 멀티 대시보드 배열
      dashboardName: config.dashboardName || config.title || (effectiveDashboardId ? `대시보드 ${effectiveDashboardId}` : '대시보드'),
      title: config.title, // 멀티 대시보드용 제목
      size: mergedConfig.size,
      position: calculatePosition(mergedConfig.position, config.parentId),
      variables: config.variables || {},
      parentId: config.parentId || null,
      onClose: config.onClose || null,
      options: mergedConfig,
      createdAt: Date.now(),
      zIndex: (popups.length > 0 ? Math.max(...popups.map(p => p.zIndex)) : 990) + 20,
      isMinimized: false,
      isMaximized: false,
      isLoading: true,
      error: null,
      // 자동 크기 모드에서 계산된 정규화 레이아웃 정보
      normalizedLayout: resolvedSize?.normalizedLayout || null,
      overrideGridColumns: resolvedSize?.gridColumns || null,
      overrideRowHeight: resolvedSize?.overrideRowHeight || null,
      // 투명 대시보드 모드: 팝업 프레임 숨기고 차트만 표시
      transparentMode: mergedConfig.transparentMode || false
    };
    
    // 부모-자식 관계 저장
    if (config.parentId) {
      parentChildMap.current[popupId] = config.parentId;
    }
    
    // 팝업 스택에 추가
    setPopups(prev => [...prev, newPopup]);
    setActivePopupId(popupId);
    
    return popupId;
  }, [popups]);

  /**
   * 팝업 열기 (외부 인터페이스)
   * @param {Object} config - 팝업 설정
   * @param {string} config.dashboardId - 표시할 대시보드 ID (단일 대시보드 모드)
   * @param {Array} config.dashboards - 멀티 대시보드 배열 (멀티 대시보드 모드)
   * @param {string} config.dashboardName - 대시보드 이름 (헤더 표시용)
   * @param {string} config.title - 팝업 제목 (멀티 대시보드용)
   * @param {Object} config.size - 팝업 크기 설정
   * @param {string} config.sizeMode - 크기 모드 ('fixed' | 'auto')
   * @param {string|Object} config.position - 팝업 위치
   * @param {Object} config.variables - 전달할 변수들
   * @param {string} config.parentId - 부모 팝업 ID (중첩 팝업의 경우)
   * @param {Function} config.onClose - 팝업 닫힐 때 콜백
   * @param {Object} config.options - 추가 옵션
   * @returns {string|null} 생성된 팝업 ID (sizeMode: 'auto'일 경우 비동기로 처리되어 null 반환)
   */
  const openPopup = useCallback((config) => {
    // config에서 유효한 dashboardId 추출 (dashboards 배열 또는 dashboardId 필드)
    const effectiveDashboardId = getEffectiveDashboardId(config);
    
    // sizeMode가 'auto'이고 유효한 dashboardId가 있으면 템플릿 조회 후 크기 계산
    if (config.sizeMode === 'auto' && effectiveDashboardId) {
      // 비동기로 템플릿 조회 후 팝업 열기
      DashboardService.getDashboardTemplate(effectiveDashboardId, true)
        .then(response => {
          let autoSize = null;
          
          if (response && response.success && response.data) {
            autoSize = calculateAutoSizeFromTemplate(response.data);
          } else {
            // 템플릿 조회 실패 시 기본 크기 사용
            autoSize = { width: '70vw', height: '65vh' };
          }
          
          // 계산된 크기로 팝업 열기
          openPopupInternal(config, autoSize);
        })
        .catch(error => {
          console.error('[DashboardPopup] 템플릿 조회 에러:', error);
          // 에러 시 기본 크기로 팝업 열기
          openPopupInternal(config, { width: '70vw', height: '65vh' });
        });
      
      // 비동기 처리이므로 null 반환
      return null;
    }
    
    // 고정 크기 모드: 기존 동기 방식으로 처리
    return openPopupInternal(config);
  }, [openPopupInternal]);
  
  /**
   * 팝업 닫기
   * @param {string} popupId - 닫을 팝업 ID
   * @param {boolean} closeChildren - 자식 팝업도 함께 닫을지 여부
   */
  const closePopup = useCallback((popupId, closeChildren = true) => {
    setPopups(prev => {
      const popup = prev.find(p => p.id === popupId);
      if (!popup) return prev;
      
      // 닫을 팝업 목록 수집
      const popupsToClose = new Set([popupId]);
      
      // 자식 팝업도 닫기
      if (closeChildren) {
        const findChildren = (parentId) => {
          prev.forEach(p => {
            if (p.parentId === parentId) {
              popupsToClose.add(p.id);
              findChildren(p.id); // 재귀적으로 모든 자손 찾기
            }
          });
        };
        findChildren(popupId);
      }
      
      // onClose 콜백 실행
      popupsToClose.forEach(id => {
        const p = prev.find(popup => popup.id === id);
        if (p && p.onClose) {
          p.onClose(id);
        }
      });
      
      // 부모-자식 관계 정리
      popupsToClose.forEach(id => {
        delete parentChildMap.current[id];
        delete popupRefs.current[id];
      });
      
      // 팝업 제거
      const remainingPopups = prev.filter(p => !popupsToClose.has(p.id));
      
      // 활성 팝업 재설정
      if (popupsToClose.has(activePopupId)) {
        const newActiveId = remainingPopups.length > 0 
          ? remainingPopups[remainingPopups.length - 1].id 
          : null;
        setActivePopupId(newActiveId);
      }
      
      console.log('[DashboardPopup] Popup(s) closed:', Array.from(popupsToClose));
      
      return remainingPopups;
    });
  }, [activePopupId]);
  
  /**
   * 모든 팝업 닫기
   */
  const closeAllPopups = useCallback(() => {
    // 모든 onClose 콜백 실행
    popups.forEach(popup => {
      if (popup.onClose) {
        popup.onClose(popup.id);
      }
    });
    
    // 상태 초기화
    setPopups([]);
    setActivePopupId(null);
    parentChildMap.current = {};
    popupRefs.current = {};
    
    console.log('[DashboardPopup] All popups closed');
  }, [popups]);
  
  /**
   * 팝업 업데이트
   * @param {string} popupId - 업데이트할 팝업 ID
   * @param {Object} updates - 업데이트할 속성들
   */
  const updatePopup = useCallback((popupId, updates) => {
    setPopups(prev => prev.map(popup => {
      if (popup.id === popupId) {
        return { ...popup, ...updates };
      }
      return popup;
    }));
  }, []);
  
  /**
   * 팝업 위치 업데이트
   */
  const updatePopupPosition = useCallback((popupId, position) => {
    updatePopup(popupId, { position });
  }, [updatePopup]);
  
  /**
   * 팝업 크기 업데이트
   */
  const updatePopupSize = useCallback((popupId, size) => {
    updatePopup(popupId, { size });
  }, [updatePopup]);
  
  /**
   * 팝업 최소화/최대화
   */
  const toggleMinimize = useCallback((popupId) => {
    setPopups(prev => prev.map(popup => {
      if (popup.id === popupId) {
        return { 
          ...popup, 
          isMinimized: !popup.isMinimized,
          isMaximized: false 
        };
      }
      return popup;
    }));
  }, []);
  
  const toggleMaximize = useCallback((popupId) => {
    setPopups(prev => prev.map(popup => {
      if (popup.id === popupId) {
        return { 
          ...popup, 
          isMaximized: !popup.isMaximized,
          isMinimized: false 
        };
      }
      return popup;
    }));
  }, []);
  
  /**
   * 팝업 포커스 설정
   */
  const focusPopup = useCallback((popupId) => {
    // parentChildMap ref를 사용하여 자식 팝업 존재 여부 확인
    // parentChildMap은 { [childId]: parentId } 형태이므로 값을 순회하여 확인
    const hasChildPopup = Object.values(parentChildMap.current).includes(popupId);
    
    // 자식 팝업이 있으면 z-index를 올리지 않음 (자식이 가려지는 것 방지)
    if (hasChildPopup) {
      setActivePopupId(popupId);
      return;
    }

    setActivePopupId(popupId);
    
    // z-index 재정렬
    setPopups(prev => {
      const popup = prev.find(p => p.id === popupId);
      if (!popup) return prev;
      
      // 상태 기반으로도 한 번 더 체크 (이중 방어)
      if (prev.some(p => p.parentId === popupId)) {
        return prev;
      }
      
      const maxZIndex = Math.max(...prev.map(p => p.zIndex));
      
      if (popup.zIndex === maxZIndex) {
        return prev;
      }
      
      return prev.map(p => {
        if (p.id === popupId) {
          return { ...p, zIndex: maxZIndex + 10 };
        }
        return p;
      });
    });
  }, []);
  
  /**
   * 팝업 로딩 상태 설정
   */
  const setPopupLoading = useCallback((popupId, isLoading) => {
    updatePopup(popupId, { isLoading });
  }, [updatePopup]);
  
  /**
   * 팝업 에러 설정
   */
  const setPopupError = useCallback((popupId, error) => {
    updatePopup(popupId, { error, isLoading: false });
  }, [updatePopup]);
  
  /**
   * 팝업 로드 완료 설정
   */
  const setPopupLoadComplete = useCallback((popupId) => {
    console.log(`[DashboardPopupContext] 팝업 로드 완료: ${popupId}`);
    updatePopup(popupId, { isLoading: false });
  }, [updatePopup]);
  
  /**
   * 팝업 존재 여부 확인
   * dashboards 배열과 dashboardId 필드 모두 지원
   */
  const hasPopup = useCallback((dashboardId) => {
    if (!dashboardId) return false;
    return popups.some(p => {
      const popupDashboardId = getEffectiveDashboardId(p);
      return popupDashboardId === dashboardId;
    });
  }, [popups]);
  
  /**
   * 팝업 가져오기
   */
  const getPopup = useCallback((popupId) => {
    return popups.find(p => p.id === popupId);
  }, [popups]);
  
  /**
   * 팝업 레퍼런스 설정
   */
  const setPopupRef = useCallback((popupId, ref) => {
    popupRefs.current[popupId] = ref;
  }, []);
  
  // openDashboard는 openPopup의 별칭 (기존 코드 호환성)
  const openDashboard = openPopup;

  const value = {
    popups,
    activePopupId,
    openPopup,
    openDashboard, // 기존 코드 호환성을 위한 별칭
    closePopup,
    closeAllPopups,
    updatePopup,
    updatePopupPosition,
    updatePopupSize,
    toggleMinimize,
    toggleMaximize,
    focusPopup,
    setPopupLoading,
    setPopupError,
    setPopupLoadComplete,
    hasPopup,
    getPopup,
    setPopupRef,
    maxPopups: MAX_POPUPS
  };
  
  return (
    <DashboardPopupContext.Provider value={value}>
      {children}
    </DashboardPopupContext.Provider>
  );
};

export default DashboardPopupContext;